/**
 * signInAnalytics — the GA4 `login` event, logged where an interactive sign-in
 * COMPLETES, from its `UserCredential`.
 *
 * It used to be logged from the auth bootstrap (`onAuthStateChanged` in
 * useAuth). That callback also fires when a stored session is restored, so
 * `login` counted app starts and gym switches, not sign-ins — in production it
 * outnumbered `session_start` (1973 vs 1567 over 90 days). A credential only
 * exists when the person actually signed in, which is the thing the event
 * name promises.
 *
 * Skipped:
 *   - a brand-new auth user — the bootstrap's `sign_up` covers that sign-in;
 *   - anything that is not a sign-in (`link`, `reauthenticate`) — e.g. a
 *     provider linked through a redirect on the profile page;
 *   - the same credential a second time — `redirectResultOnce()` hands ONE
 *     credential to every `useAuth` mount, and two can be mounted over a boot.
 */
import { getAdditionalUserInfo } from "firebase/auth";
import { logEvent, EVT } from "../analytics";

const logged = new WeakSet();

/** The `method` param for an OAuth credential, from its provider id. */
export function signInMethodForProvider(providerId) {
  if (providerId === "google.com") return "google";
  if (providerId === "apple.com") return "apple";
  return null;
}

/**
 * @param {import("firebase/auth").UserCredential | null | undefined} cred
 * @param {"google"|"apple"|"email_link"|"password"|null} method
 * @returns {boolean} whether a `login` event was logged
 */
export function logInteractiveSignIn(cred, method) {
  if (!cred || typeof cred !== "object") return false;
  if (cred.operationType !== "signIn") return false;
  if (logged.has(cred)) return false;
  logged.add(cred);
  let isNewUser = false;
  try {
    isNewUser = getAdditionalUserInfo(cred)?.isNewUser === true;
  } catch {
    // An unreadable credential is still a completed sign-in.
  }
  if (isNewUser) return false;
  logEvent(EVT.LOGIN, method ? { method } : {});
  return true;
}

/**
 * The `login` event for a popup sign-in whose answer arrived after the SDK had
 * already declared the popup closed (see popupAuthOutcome.js). There is no
 * credential then — the SDK threw instead of handing it over — so the signed-in
 * user stands in for it. A brand-new auth user is recognised by its metadata:
 * Firebase stamps both times from the same sign-up, and the bootstrap's
 * `sign_up` covers that sign-in.
 *
 * @param {import("firebase/auth").User | null | undefined} user
 * @param {"google"|"apple"|null} method
 * @returns {boolean} whether a `login` event was logged
 */
export function logLateInteractiveSignIn(user, method) {
  if (!user || typeof user !== "object") return false;
  const { creationTime, lastSignInTime } = user.metadata || {};
  if (creationTime && creationTime === lastSignInTime) return false;
  logEvent(EVT.LOGIN, method ? { method } : {});
  return true;
}
